import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, ChevronRight, Check } from 'lucide-react';
import { useKiosk } from '../../context/KioskContext';
import { formatCurrency } from '../../utils/formatCurrency';

/**
 * CartSummaryBar - Sticky bottom bar showing cart summary
 * 
 * Features:
 * - Always visible when there are items in cart
 * - Shows: item count, subtotal, View Cart button
 * - Optional: Last added item preview
 * - Animated entrance
 */
export default function CartSummaryBar({
  onViewCart,
  lastAddedItem = null,
  className = '',
}) {
  const navigate = useNavigate();
  const { cart, getCartSubtotal } = useKiosk();
  const [showLastAdded, setShowLastAdded] = useState(false);

  const cartCount = cart.reduce((sum, item) => sum + (item.quantity || 1), 0);
  const cartSubtotal = getCartSubtotal();

  // Show last added item briefly
  useEffect(() => {
    if (lastAddedItem) {
      setShowLastAdded(true);
      const timer = setTimeout(() => setShowLastAdded(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [lastAddedItem]);

  // Don't render if cart is empty
  if (cartCount === 0) return null;

  const handleViewCart = () => {
    if (onViewCart) {
      onViewCart();
    } else {
      navigate('cart');
    }
  };

  return (
    <div 
      className={`
        fixed bottom-0 left-0 right-0 z-40
        bg-[#1a1f26]/98 backdrop-blur-md
        border-t border-white/10
        shadow-[0_-4px_20px_rgba(0,0,0,0.3)]
        ${className}
      `}
      role="complementary"
      aria-label="Cart summary"
    >
      {/* Last Added Item Preview */}
      {showLastAdded && lastAddedItem && (
        <div className="px-6 py-3 bg-[#10b981]/10 border-b border-[#10b981]/20 animate-slide-down">
          <div className="max-w-4xl mx-auto flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-[#10b981] flex items-center justify-center flex-shrink-0">
              <Check size={18} className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-[#10b981] font-medium truncate">
                Added: {lastAddedItem.name}
              </p>
            </div>
            <span className="text-sm font-semibold text-[#10b981]">
              {formatCurrency(lastAddedItem.totalPrice || lastAddedItem.price)}
            </span>
          </div>
        </div>
      )}

      {/* Main Cart Bar */}
      <div className="px-6 py-4">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={handleViewCart}
            className="
              w-full h-16 px-6
              flex items-center justify-between
              bg-gradient-to-r from-[#f5a623] to-[#ff6b35]
              text-[#0f1419]
              rounded-2xl
              shadow-lg shadow-[#f5a623]/30
              transition-all duration-200
              hover:shadow-xl hover:shadow-[#f5a623]/40 hover:brightness-110
              active:scale-[0.99]
            "
            aria-label={`View cart with ${cartCount} items totaling ${formatCurrency(cartSubtotal)}`}
          >
            {/* Left - Cart Icon + Count */}
            <div className="flex items-center gap-3">
              <div className="relative">
                <ShoppingCart size={24} />
                <span className="
                  absolute -top-2 -right-2 
                  min-w-[22px] h-[22px] 
                  flex items-center justify-center 
                  bg-[#0f1419] text-[#f5a623] 
                  text-xs font-bold rounded-full
                  px-1
                ">
                  {cartCount > 99 ? '99+' : cartCount}
                </span>
              </div>
              <div className="text-left">
                <span className="font-semibold text-lg">
                  View Cart
                </span>
                <span className="hidden sm:inline text-sm opacity-80 ml-2">
                  ({cartCount} {cartCount === 1 ? 'item' : 'items'})
                </span>
              </div>
            </div>

            {/* Right - Subtotal + Arrow */}
            <div className="flex items-center gap-2">
              <span className="font-bold text-xl">
                {formatCurrency(cartSubtotal)}
              </span>
              <ChevronRight size={24} />
            </div>
          </button>
        </div>
      </div>

      {/* Animation Styles */}
      <style>{`
        @keyframes slide-down {
          from {
            opacity: 0;
            transform: translateY(-100%);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-slide-down {
          animation: slide-down 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}
